import { Component, EventEmitter, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ControlValueAccessorModel, getProvider, getValidatorProvider } from '../../../Forms/models/control-value-accessor.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';

import { BrandSocialLink } from '../../../../api/brandIdentity/models/brand-identity.api.model';
import { brandSocialLinkToLabelStringMap, BrandSocialLinkTypeEnum } from '../../enums/brand-social-link-type.enum';

@Component({
    selector: 'agpr-brand-social-links',
    templateUrl: './brand-social-links.component.html',
    styleUrls: ['./brand-social-links.component.scss'],
    providers: [
        getProvider(BrandSocialLinksComponent),
        getValidatorProvider(BrandSocialLinksComponent)
    ],
    standalone: false
})
export class BrandSocialLinksComponent extends ControlValueAccessorModel<BrandSocialLink[]> implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly URL_REGEXP = /^(http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@%!\$&'\(\)\*\+,;=.]+$/;
    readonly linkTypesOptions: DropdownOptionModel[] = Object.values(BrandSocialLinkTypeEnum).map(
        type => new DropdownOptionModel(brandSocialLinkToLabelStringMap[type], type)
    );

    @Input() id: string = 'brandSocialLinks';
    @Input() maxLimit: number = 10;
    @Input() disabled: boolean = false;

    @ViewChildren('listItem') listItem: QueryList<any>;

    private subscriptions: Subscription[] = [];
    form: FormGroup;


    constructor(
        private fb: FormBuilder,
    ) {
        super();
    }

    ngOnInit() {
        this.form = this.fb.group({socialLinks: this.fb.array<FormGroup>([])});

        this.subscriptions.push(
            this.form.valueChanges.subscribe(() => this.emitValue())
        );
    }

    get socialLinks(): FormArray {
        return this.form.get('socialLinks') as FormArray;
    }

    add() {
        if (this.disabled || this.socialLinks.length >= this.maxLimit) {
            return;
        }
        this.socialLinks.push(this.createControl());
        requestAnimationFrame(() => {
            this.listItem.last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        });
    }

    remove(index: number) {
        this.socialLinks.removeAt(index);
    }

    createControl(value?: BrandSocialLink): FormGroup {
        return this.fb.group({
            id: [value?.id ?? null],
            type: [value?.type ?? BrandSocialLinkTypeEnum.TWITTER],
            url: [value?.url?.trim() ?? '', {updateOn: 'blur', validators: [Validators.required, Validators.pattern(this.URL_REGEXP)]}],
            text: [value?.text?.trim() ?? '', { updateOn: 'blur' }]
        });
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.form.invalid ? { invalid: true } : null;
    }

    emitValue() {
        this.value = this.socialLinks.value;
        this.onModelChange(this.value);
        this.onModelTouched();
    }

    override writeValue(value: BrandSocialLink[]): void {
        this.value = value ?? [];
        this.socialLinks.clear({ emitEvent: false });
        this.value.forEach(item => this.socialLinks.push(this.createControl(item), { emitEvent: false }));
    }

    override setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
